import {
  FoodDetailsContent,
  FoodDetailsHeader,
  FoodDetailsInfo,
  StyledFoodDetails,
  IngredientList,
  FoodDetailsPopover,
  FoodDetailsBody,
  IngredientListTitle,
} from "./styled"
import { BackButton } from "components/Common/BackButton"
import NutrientInformation from "components/foodDetails/share/NutrientInformation"
import Button from "components/Buttons/Button"
import {
  useMyFoodDetails,
  useFoodDetailsOnBack,
} from "../hooks/myFood/useMyFoodDetails"
import { Fragment, useState } from "react"
import FullPageLoading from "components/Layouts/FullPageLoading"
import { useBackToHome, useSetMealTypeParams } from "features/meal/hooks"
import { FoodListDivider } from "components/foodItem/FoodListItem/styled"
import { Action } from "antd-mobile/es/components/action-sheet"
import { generatePath, useNavigate } from "react-router-dom"
import PATHS from "router/paths"
import PopConfirm from "components/PopConfirm"
import { ConfirmEntry } from "components/PopConfirm/styled"
import { useDeleteFood } from "../hooks"
import { FlexBox } from "components/Common/FlexBox"
import TrashIcon from "components/Icons/TrashIcon"
import EditIcon from "components/Icons/EditIcon"
import { TopNavWrapper } from "components/Layouts/TopNavWrapper"
import { FOOD_DETAILS_BODY_ID } from "config"
import HorizontalThreeDots from "components/Icons/HorizontalThreeDots"
import ChevronLeftBtn from "components/Icons/ChevronLeftBtn"
import { useSetPageTitle } from "libs/ga"
import ScheduleConfigFormSection from "components/foodDetails/share/ScheduleConfigFormSection"
import useScheduleFormSection from "features/schedule/hooks/useScheduleFormSection"
import { validateScheduleConfig } from "utils/validator"
import { useCreateSchedule } from "features/schedule/hooks/CRUDSchedule"
import useGramFromIngredients from "../hooks/useGramFromIngredients"
import { IMyFood } from "model/food/types/myfood.types"
import useMyFoodDetailsForm from "../hooks/myFood/useMyFoodDetailsForm"
import MyFoodNameAndPortion from "components/foodDetails/share/MyFoodNameAndPortion"
import ViewOnlyIngredient from "components/foodDetails/share/ViewOnlyIngredient"
import useLogMealFromFood from "features/meal/hooks/logMeal/useLogMealFromFood"
import FoodImage from "components/foodDetails/share/FoodImage"
import FoodDetailsMealTypeSelection from "components/mealTypeSelection/FoodDetailsMealTypeSelection"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"

const actions: Action[] = [
  {
    key: "edit",
    text: (
      <FlexBox $alignItems="center" $gap={8}>
        <EditIcon /> Edit
      </FlexBox>
    ),
  },
  {
    key: "delete",
    text: (
      <FlexBox $alignItems="center" $gap={8}>
        <TrashIcon color="#FF4921" /> Delete
      </FlexBox>
    ),
  },
]

const MyFoodDetails = () => {
  useSetPageTitle("Food Details")
  const { foodDetails, getFoodDetailsLoading } = useMyFoodDetails()
  const { myFood, onPortionChange, onUnitChange } = useMyFoodDetailsForm(foodDetails)

  const gram = useGramFromIngredients(myFood)

  const {
    onDayOfWeekChange,
    onSchedulePeriodChange,
    onScheduleTypeChange,
    scheduleConfig,
    isNoEndDate,
    onNoEndDateChange,
    getCreateSchedulePayloadFromFood,
  } = useScheduleFormSection()

  const [hasSchedule, setHasSchedule] = useState(false)
  const { mealTypeValue, setMealType } = useSetMealTypeParams()

  const [isPortionValid, setIsPortionValid] = useState(true)
  const handlePortionChange = (value: number) => {
    onPortionChange(value)
    setIsPortionValid(true)
  }

  const backToHome = useBackToHome()

  const { handleLogMealFromFood, logMealLoading } =
    useLogMealFromFood(backToHome)

  const { handleCreateSchedule, createScheduleLoading } =
    useCreateSchedule(backToHome)

  const onBack = useFoodDetailsOnBack()

  const navigate = useNavigate()

  const goToEdit = () => {
    if (foodDetails) {
      navigate(generatePath(PATHS.app.food.edit, { id: foodDetails.id }))
    }
  }

  const [isPopConfirmDeleteOpen, setIsPopConfirmDeleteOpen] = useState(false)
  const { handleDeleteFood, deleteFoodLoading } = useDeleteFood(onBack)
  const previewImageUrl = foodDetails?.previewImageUrl ?? foodDetails?.imageUrl

  const [isPopConfirmScheduleOpen, setIsPopConfirmScheduleOpen] =
    useState(false)

  const onSubmit = (food: IMyFood) => {
    if (hasSchedule) {
      const schedulePayload = getCreateSchedulePayloadFromFood(food)

      if (schedulePayload) {
        handleCreateSchedule(schedulePayload)
      }
    } else {
      handleLogMealFromFood(food)
    }
  }

  const isScheduleValid = hasSchedule
    ? validateScheduleConfig(scheduleConfig, !isNoEndDate)
    : true

  return (
    <Fragment>
      {getFoodDetailsLoading || !myFood ? (
        <FullPageLoading />
      ) : (
        <StyledFoodDetails>
          <FoodDetailsContent>
            <FoodDetailsBody id={FOOD_DETAILS_BODY_ID}>
              <FoodImage previewImageUrl={previewImageUrl}>
                <TopNavWrapper>
                  <FoodDetailsHeader>
                    <BackButton onClick={onBack}>
                      <ChevronLeftBtn />
                    </BackButton>

                    <FoodDetailsMealTypeSelection
                      value={mealTypeValue}
                      onChange={setMealType}
                    />
                    <div style={{ width: 24, height: 24 }}></div>
                  </FoodDetailsHeader>
                </TopNavWrapper>
              </FoodImage>

              <FoodDetailsInfo>
                <MyFoodNameAndPortion
                  food={myFood}
                  unitValue={myFood.unit}
                  portionValue={myFood.portion}
                  onPortionChange={handlePortionChange}
                  onUnitChange={onUnitChange}
                  setIsPortionValid={setIsPortionValid}
                  calories={myFood.calorie}
                  gram={gram}
                />

                <NutrientInformation entry={myFood} />

                <ScheduleConfigFormSection
                  scheduleConfig={scheduleConfig}
                  onDayOfWeekChange={onDayOfWeekChange}
                  onSchedulePeriodChange={onSchedulePeriodChange}
                  onScheduleTypeChange={onScheduleTypeChange}
                  isNoEndDate={isNoEndDate}
                  onNoEndDateChange={onNoEndDateChange}
                  hasSchedule={hasSchedule}
                  setHasSchedule={setHasSchedule}
                />

                <IngredientList>
                  <IngredientListTitle>Ingredients</IngredientListTitle>{" "}
                  {myFood.ingredients.map((ingredient, index) => (
                    <div key={index}>
                      <ViewOnlyIngredient ingredient={ingredient} />
                      <FoodListDivider />
                    </div>
                  ))}
                </IngredientList>
              </FoodDetailsInfo>
            </FoodDetailsBody>
            <AppStyleCtaBox>
              <FlexBox style={{ width: "100%" }} $gap={16}>
                <FoodDetailsPopover
                  actions={actions}
                  placement="top-start"
                  onAction={({ key }) => {
                    if (key === "edit") {
                      goToEdit()
                    }

                    if (key === "delete") {
                      setIsPopConfirmDeleteOpen(true)
                    }
                  }}
                  trigger="click"
                >
                  <Button
                    style={{
                      padding: "0 15px",
                      height: 48,
                      borderColor: "rgba(0, 38, 58, 0.14)",
                    }}
                    $isOption={true}
                    $size="small"
                    $type="default"
                  >
                    <HorizontalThreeDots />
                  </Button>
                </FoodDetailsPopover>
                <Button
                  loading={logMealLoading}
                  $type="primary"
                  style={{ width: "100%" }}
                  disabled={!isPortionValid || !isScheduleValid}
                  onClick={() => {
                    if (hasSchedule) {
                      setIsPopConfirmScheduleOpen(true)
                    } else {
                      onSubmit(myFood)
                    }
                  }}
                >
                  Log this item
                </Button>
              </FlexBox>
            </AppStyleCtaBox>
          </FoodDetailsContent>

          <PopConfirm
            isOpen={isPopConfirmDeleteOpen}
            title="Delete meal"
            onCancel={() => setIsPopConfirmDeleteOpen(false)}
            onConfirm={() => {
              handleDeleteFood(foodDetails?.id as string)
            }}
            type="danger"
            loading={deleteFoodLoading}
          >
            <div style={{ textAlign: "center" }}>
              Are you sure you want to remove {""}
              <ConfirmEntry>{foodDetails?.name}</ConfirmEntry>. from My Foods?
              Once deleted, it will not longer be saved in your list.
            </div>
          </PopConfirm>

          <PopConfirm
            cancelText="No"
            confirmText="Yes"
            isOpen={isPopConfirmScheduleOpen}
            title="Add Scheduled Meal?"
            onCancel={() => setIsPopConfirmScheduleOpen(false)}
            onConfirm={() => {
              onSubmit(myFood)
            }}
            type="info"
            loading={createScheduleLoading}
          >
            <div style={{ textAlign: "center" }}>
              This meal will be logged automatically on your selected days. Do
              you want to proceed?
            </div>
          </PopConfirm>
        </StyledFoodDetails>
      )}
    </Fragment>
  )
}

export default MyFoodDetails
